import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Users, UserPlus, Upload, Sparkles, Trash2, Search, Target, Star, Mail, Send, Pencil, Clock, Wand2, Download, FileUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const SEG_LABELS = { all: "Tous", vip: "VIP", new: "Nouveaux (30j)", inactive: "Inactifs (+60j)" };
const TYPE_LABELS = { client: "Client", fournisseur: "Fournisseur", prive: "Privé", autre: "Autre" };
const TYPE_FILTERS = { all: "Tous types", client: "Clients", fournisseur: "Fournisseurs", prive: "Privé", autre: "Autre" };

export default function ProCrmContacts() {
  const [stats, setStats] = useState({ total: 0, active: 0, vip: 0, new30: 0, inactive: 0, pending: 0 });
  const [items, setItems] = useState([]);
  const [segment, setSegment] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ first_name: "", email: "", phone: "", city: "", tags: "", birthdate: "", contact_type: "client" });
  const [raw, setRaw] = useState("");
  const [sources, setSources] = useState({ loyalty: 0, reviews: 0, redemptions: 0 });
  const [busy, setBusy] = useState(false);
  const [emailTo, setEmailTo] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: "", body: "" });
  const [aiGoal, setAiGoal] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [editTo, setEditTo] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [histTo, setHistTo] = useState(null);
  const [hist, setHist] = useState(null);

  const load = () => {
    api.get(`/pro/crm/stats`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/pro/crm/contacts`, { params: { segment, q, type: typeFilter } }).then((r) => setItems(r.data.items || [])).catch(() => {});
    api.get(`/pro/crm/sources`).then((r) => setSources(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [segment, typeFilter]);

  const sendDirectEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) { toast.error("Objet et message requis."); return; }
    setBusy(true);
    try {
      await api.post(`/pro/crm/contacts/${emailTo.id}/email`, emailForm);
      toast.success(`E-mail envoyé à ${emailTo.first_name || emailTo.email}`);
      setEmailTo(null); setEmailForm({ subject: "", body: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); }
  };

  const aiDraft = async () => {
    if (!aiGoal.trim()) { toast.error("Dites à l'IA quoi écrire (ex. « inviter à passer cette semaine »)."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post(`/pro/crm/ai/text`, { goal: aiGoal.trim(), tone: "chaleureux" });
      setEmailForm({ subject: data.subject || emailForm.subject, body: data.body || emailForm.body });
      toast.success("Brouillon généré");
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération impossible"); }
    finally { setAiBusy(false); }
  };

  const openEdit = (c) => {
    setEditForm({ first_name: c.first_name || "", last_name: c.last_name || "", email: c.email || "",
      phone: c.phone || "", city: c.city || "", tags: (c.tags || []).join(", "), birthdate: c.birthdate || "",
      contact_type: c.contact_type || "client", notes: c.notes || "" });
    setEditTo(c);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/pro/crm/contacts/${editTo.id}`, {
        ...editForm, tags: editForm.tags ? editForm.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] });
      toast.success("Contact mis à jour");
      setEditTo(null); setEditForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const openHistory = async (c) => {
    setHistTo(c); setHist(null);
    try { const { data } = await api.get(`/pro/crm/contacts/${c.id}/history`); setHist(data); }
    catch { setHist({ last_emailed_at: null, items: [] }); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const addContact = async (e) => {
    e.preventDefault();
    if (!form.email && !form.phone) { toast.error("Indiquez au moins un e-mail ou un téléphone."); return; }
    setBusy(true);
    try {
      await api.post(`/pro/crm/contacts`, { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()) : [] });
      toast.success("Contact ajouté");
      setForm({ first_name: "", email: "", phone: "", city: "", tags: "", birthdate: "", contact_type: "client" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const importRaw = async () => {
    if (!raw.trim()) { toast.error("Collez des contacts (un par ligne)."); return; }
    setBusy(true);
    try { const { data } = await api.post(`/pro/crm/contacts/import`, { raw }); toast.success(`${data.added} ajouté(s), ${data.skipped} ignoré(s)`); setRaw(""); load(); }
    catch { toast.error("Erreur d'import"); } finally { setBusy(false); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/pro/crm/contacts/import-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.added} ajouté(s), ${data.skipped} ignoré(s)`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Fichier illisible"); }
    finally { setBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/pro/crm/contacts/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = "contacts.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch { toast.error("Export impossible"); }
  };

  const importSources = async (keys) => {
    setBusy(true);
    try { const { data } = await api.post(`/pro/crm/sources/import`, { sources: keys }); toast.success(`${data.added} contact(s) récupéré(s)`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce contact ?")) return;
    try { await api.delete(`/pro/crm/contacts/${id}`); load(); } catch { toast.error("Erreur"); }
  };

  const goalPct = Math.min(100, Math.round((stats.total / 100) * 100));

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="crm-contacts-page">
      <header>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900 flex items-center gap-2"><Users size={22} className="text-[#E05638]" /> Mes contacts</h1>
        <p className="text-sm text-slate-600 mt-1">Rassemblez tous vos clients ici pour leur envoyer vos bons plans et les faire revenir.</p>
      </header>

      {/* Jauge d'objectif */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="crm-goal">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-slate-800 flex items-center gap-2"><Target size={16} className="text-[#E05638]" /> {stats.total} contact(s) — objectif 100</span>
          <span className="text-slate-500">{stats.vip} VIP · {stats.active} actifs · {stats.pending} en attente</span>
        </div>
        <div className="mt-2 h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#E05638] transition-all" style={{ width: `${goalPct}%` }} />
        </div>
      </div>

      {/* Collecte automatique */}
      {(sources.loyalty + sources.reviews + sources.redemptions) > 0 && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5" data-testid="crm-sources">
          <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><Sparkles size={18} className="text-emerald-600" /> Récupérez vos clients déjà connus</div>
          <p className="text-sm text-slate-600 mt-1">Importez en un clic les contacts issus de vos autres outils PROXIreduc-28.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {sources.loyalty > 0 && <Button variant="outline" size="sm" onClick={() => importSources(["loyalty"])} disabled={busy} data-testid="crm-import-loyalty">Fidélité ({sources.loyalty})</Button>}
            {sources.reviews > 0 && <Button variant="outline" size="sm" onClick={() => importSources(["reviews"])} disabled={busy} data-testid="crm-import-reviews">Avis ({sources.reviews})</Button>}
            {sources.redemptions > 0 && <Button variant="outline" size="sm" onClick={() => importSources(["redemptions"])} disabled={busy} data-testid="crm-import-redemptions">Offres utilisées ({sources.redemptions})</Button>}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* Ajout manuel */}
        <form onSubmit={addContact} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="crm-add-form">
          <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><UserPlus size={18} className="text-[#E05638]" /> Ajouter un contact</div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div><Label>Prénom</Label><Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="crm-first-name" /></div>
            <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} /></div>
            <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="crm-email" /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div>
            <div><Label>Étiquettes (séparées par ,)</Label><Input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="VIP, fidèle" /></div>
            <div><Label>Anniversaire</Label><Input type="date" value={form.birthdate} onChange={(e) => setForm((f) => ({ ...f, birthdate: e.target.value }))} /></div>
            <div className="col-span-2"><Label>Type de contact</Label>
              <select value={form.contact_type} onChange={(e) => setForm((f) => ({ ...f, contact_type: e.target.value }))} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="crm-contact-type">
                {Object.keys(TYPE_LABELS).map((k) => <option key={k} value={k}>{TYPE_LABELS[k]}</option>)}
              </select>
            </div>
          </div>
          <Button type="submit" disabled={busy} className="mt-3 btn-shine text-white" data-testid="crm-add-submit">Ajouter</Button>
        </form>

        {/* Import copier-coller */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="crm-import">
          <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><Upload size={18} className="text-slate-500" /> Importer en masse</div>
          <p className="text-sm text-slate-600 mt-1">Collez vos contacts : un par ligne (prénom ; e-mail ; ville).</p>
          <Textarea rows={5} value={raw} onChange={(e) => setRaw(e.target.value)} className="mt-2" placeholder={"Marie ; marie@mail.com ; Chartres\nJean ; jean@mail.com"} data-testid="crm-import-raw" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={importRaw} disabled={busy} data-testid="crm-import-submit">Importer le texte</Button>
            <label className="inline-flex">
              <input type="file" accept=".csv,.xlsx,.xlsm,text/csv" className="hidden" onChange={importFile} disabled={busy} data-testid="crm-import-file-input" />
              <span className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 text-sm font-medium cursor-pointer hover:bg-slate-50 ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="crm-import-file-btn"><FileUp size={15} /> Importer un fichier (CSV / Excel)</span>
            </label>
          </div>
          <p className="text-xs text-slate-400 mt-2">Colonnes reconnues : Prénom, Nom, E-mail, Téléphone, Ville, Type, Étiquettes, Notes.</p>
        </div>
      </div>

      {/* Liste + filtres */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex gap-1 flex-wrap">
            {Object.keys(SEG_LABELS).map((s) => (
              <button key={s} onClick={() => setSegment(s)} data-testid={`crm-seg-${s}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${segment === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                {SEG_LABELS[s]}{s === "vip" ? ` (${stats.vip})` : s === "new" ? ` (${stats.new30})` : s === "inactive" ? ` (${stats.inactive})` : ""}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} className="gap-1.5" data-testid="crm-export-btn"><Download size={15} /> Exporter</Button>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid="crm-type-filter">
              {Object.keys(TYPE_FILTERS).map((k) => <option key={k} value={k}>{TYPE_FILTERS[k]}</option>)}
            </select>
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Rechercher…" className="w-40" data-testid="crm-search" />
            <Button variant="outline" size="icon" onClick={load}><Search size={16} /></Button>
          </div>
        </div>
        {items.length === 0 ? (
          <div className="mt-4 text-sm text-slate-500">Aucun contact dans ce filtre.</div>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100" data-testid="crm-contacts-list">
            {items.map((c) => (
              <li key={c.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                    {c.first_name || c.email || c.phone}
                    {(c.tags || []).includes("VIP") && <Star size={13} className="text-amber-500 fill-amber-500" />}
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold">{TYPE_LABELS[c.contact_type] || "Client"}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}{c.city ? ` · ${c.city}` : ""} · {c.status}{c.last_emailed_at ? ` · dernier e-mail ${fmtDate(c.last_emailed_at)}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => openHistory(c)} title="Historique des échanges" className="text-slate-400 hover:text-slate-700" data-testid={`crm-hist-${c.id}`}><Clock size={16} /></button>
                  <button onClick={() => openEdit(c)} title="Modifier le contact" className="text-slate-400 hover:text-slate-700" data-testid={`crm-edit-${c.id}`}><Pencil size={16} /></button>
                  {c.email && c.status !== "unsubscribed" && (
                    <button onClick={() => { setEmailTo(c); setEmailForm({ subject: "", body: "" }); setAiGoal(""); }} title="Envoyer un e-mail" className="text-slate-400 hover:text-[#E05638]" data-testid={`crm-email-${c.id}`}><Mail size={16} /></button>
                  )}
                  <button onClick={() => del(c.id)} className="text-slate-400 hover:text-red-500" data-testid={`crm-del-${c.id}`}><Trash2 size={16} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modale : envoi d'un e-mail à UN seul contact */}
      <Dialog open={!!emailTo} onOpenChange={(o) => { if (!o) setEmailTo(null); }}>
        <DialogContent data-testid="crm-direct-email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={18} className="text-[#E05638]" /> E-mail à {emailTo?.first_name || emailTo?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">Destinataire : {emailTo?.email}</div>
            <div className="rounded-lg border border-dashed border-[#E05638]/40 bg-[#FFF7ED] p-3">
              <Label className="text-[#9A3412] flex items-center gap-1.5"><Wand2 size={14} /> Rédiger avec l'IA</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={aiGoal} onChange={(e) => setAiGoal(e.target.value)} placeholder="Ex : inviter à passer cette semaine" data-testid="crm-direct-ai-goal" />
                <Button type="button" variant="outline" onClick={aiDraft} disabled={aiBusy} className="shrink-0" data-testid="crm-direct-ai-btn">{aiBusy ? <span className="flex items-center gap-1.5"><Wand2 size={14} className="animate-pulse" /> Rédaction…</span> : "Générer"}</Button>
              </div>
            </div>
            <div><Label>Objet</Label><Input value={emailForm.subject} onChange={(e) => setEmailForm((f) => ({ ...f, subject: e.target.value }))} data-testid="crm-direct-subject" /></div>
            <div><Label>Message <span className="text-xs text-slate-400">({"{prenom}"} possible)</span></Label>
              <Textarea rows={6} value={emailForm.body} onChange={(e) => setEmailForm((f) => ({ ...f, body: e.target.value }))} data-testid="crm-direct-body" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailTo(null)}>Annuler</Button>
            <Button onClick={sendDirectEmail} disabled={busy} className="btn-shine text-white" data-testid="crm-direct-send"><Send size={15} className="mr-1" /> Envoyer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale : édition complète d'un contact */}
      <Dialog open={!!editTo} onOpenChange={(o) => { if (!o) { setEditTo(null); setEditForm(null); } }}>
        <DialogContent data-testid="crm-edit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil size={18} className="text-[#E05638]" /> Modifier le contact</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom</Label><Input value={editForm.first_name} onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="crm-edit-first-name" /></div>
              <div><Label>Nom</Label><Input value={editForm.last_name} onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))} /></div>
              <div><Label>E-mail</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} data-testid="crm-edit-email" /></div>
              <div><Label>Téléphone</Label><Input value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} /></div>
              <div><Label>Ville</Label><Input value={editForm.city} onChange={(e) => setEditForm((f) => ({ ...f, city: e.target.value }))} /></div>
              <div><Label>Anniversaire</Label><Input type="date" value={editForm.birthdate} onChange={(e) => setEditForm((f) => ({ ...f, birthdate: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Type de contact</Label>
                <select value={editForm.contact_type} onChange={(e) => setEditForm((f) => ({ ...f, contact_type: e.target.value }))} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="crm-edit-type">
                  {Object.keys(TYPE_LABELS).map((k) => <option key={k} value={k}>{TYPE_LABELS[k]}</option>)}
                </select>
              </div>
              <div className="col-span-2"><Label>Étiquettes (séparées par ,)</Label><Input value={editForm.tags} onChange={(e) => setEditForm((f) => ({ ...f, tags: e.target.value }))} placeholder="VIP, fidèle" data-testid="crm-edit-tags" /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} data-testid="crm-edit-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditTo(null); setEditForm(null); }}>Annuler</Button>
            <Button onClick={saveEdit} disabled={busy} className="btn-shine text-white" data-testid="crm-edit-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale : historique des échanges */}
      <Dialog open={!!histTo} onOpenChange={(o) => { if (!o) { setHistTo(null); setHist(null); } }}>
        <DialogContent data-testid="crm-history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock size={18} className="text-[#E05638]" /> Échanges avec {histTo?.first_name || histTo?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">Dernier e-mail envoyé : <strong>{fmtDate(hist?.last_emailed_at)}</strong></div>
            {!hist ? (
              <div className="text-sm text-slate-400">Chargement…</div>
            ) : (hist.items || []).length === 0 ? (
              <div className="text-sm text-slate-500">Aucun e-mail envoyé à ce contact pour l'instant.</div>
            ) : (
              <ul className="divide-y divide-slate-100 max-h-80 overflow-auto" data-testid="crm-history-list">
                {hist.items.map((m, i) => (
                  <li key={i} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{m.subject}</div>
                      <div className="text-xs text-slate-500">{fmtDate(m.date)}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.kind === "direct" ? "bg-[#FFF7ED] text-[#9A3412]" : "bg-slate-100 text-slate-600"}`}>{m.kind === "direct" ? "Individuel" : "Campagne"}</span>
                      {m.opened && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">Ouvert</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
